<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { ...base, unelevated: true, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrSkeleton from './HrSkeleton.vue';

/**
 * Icon button primitive with M3 variants (standard/filled/tonal/outlined) and size tokens.
 *
 * @status internal
 * @group actions
 * @see https://quasar.dev/vue-components/button Quasar q-btn API
 */
/*
 * Attrs are spread into effectiveProps by hand — class and style with them — so automatic
 * fallthrough would apply each one twice. Twice is harmless for most of them and not for the click
 * listener, which an unavailable button has to be able to withhold.
 */
defineOptions({ name: 'HrIconBtn', inheritAttrs: false });

// Every way q-btn can be given somewhere to go, and so every way it can be activated by the browser.
const DESTINATION_PROPS = ['to', 'href', 'target', 'replace'];

const props = defineProps({
  /**
   * The icon to display. Required — icon buttons have no label.
   */
  icon: {
    type: String,
    required: true,
  },
  /**
   * Visual emphasis variant derived from the M3 icon button hierarchy.
   * @values standard, filled, tonal, outlined
   */
  variant: {
    type: String,
    default: 'standard',
    validator: (v) => ['standard', 'filled', 'tonal', 'outlined'].includes(v),
  },
  /**
   * Size of the button, controlling the container and icon dimensions.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Width of the button container relative to the square default.
   * @values narrow, default, wide
   */
  width: {
    type: String,
    default: 'default',
    validator: (v) => ['narrow', 'default', 'wide'].includes(v),
  },
  /**
   * Shape of the button container.
   * @values round, square
   */
  shape: {
    type: String,
    default: 'round',
    validator: (v) => ['round', 'square'].includes(v),
  },
  /**
   * Whether the button is disabled.
   */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * The action cannot be taken right now and the button carries the reason — in its tooltip, its
   * aria-label, or both. It reads as disabled and does nothing, but stays hoverable and focusable
   * so the reason is reachable: a native disabled button receives no mouse events, so a tooltip on
   * one never opens, and a reader is left with a greyed control and no way to find out why.
   *
   * `disable` is still the right prop when there is nothing to explain. Where both are set,
   * `disable` wins.
   */
  unavailable: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the button is in a loading state.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Switches the color role from primary to error. Use for destructive actions.
   */
  destructive: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders a skeleton placeholder instead of the button.
   * Use when the button's action is not yet available.
   */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Vue Router route location. When set the button renders as a router-link.
   */
  to: {
    type: [String, Object],
    default: null,
  },
  /**
   * Replace the current history entry instead of pushing a new one.
   */
  replace: {
    type: Boolean,
    default: false,
  },
  /**
   * Native anchor href. Use for external links; prefer `to` for in-app navigation.
   */
  href: {
    type: String,
    default: null,
  },
  /**
   * Native anchor target (e.g. '_blank'). Only meaningful when `href` is set.
   */
  target: {
    type: String,
    default: null,
  },
  /**
   * Quasar props forwarded to q-btn when the legacy theme is active. Ignored entirely under the
   * design-system theme. Use this to pass visual/behavioral Quasar props (e.g. flat, color,
   * dense) that have no equivalent in the new design system.
   *
   * Note: this is distinct from $attrs. legacyProps carries Quasar-specific appearance props and
   * is silenced in the new theme. $attrs carries event listeners, aria-* and data-* attributes,
   * and is always forwarded to q-btn regardless of theme. Both channels are needed and serve
   * different lifecycles — legacyProps disappears when the legacy theme is removed, $attrs stays.
   *
   * Semantic wrappers (e.g. HrIconActionBtn) inject their own Quasar defaults here and may expose
   * this prop so callers can add further overrides. The wrapper must bind :legacy-props AFTER
   * v-bind="$props" so the merged defaults win over the raw passed-in value.
   *
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const isUnavailable = computed(() => props.unavailable && !props.disable);

/*
 * Taken out rather than suppressed in the handler: the listener reaches q-btn through $attrs, so a
 * handler of this component's own would run beside the caller's instead of in place of it.
 */
const attrsWithoutClick = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => key !== 'onClick'),
));

const classes = computed(() => [
  'hr-icon-btn',
  `hr-icon-btn--${props.variant}`,
  `hr-icon-btn--size-${props.size}`,
  `hr-icon-btn--width-${props.width}`,
  `hr-icon-btn--shape-${props.shape}`,
  props.destructive && 'hr-icon-btn--destructive',
]);

// Two prop channels reach q-btn:
//   legacyProps — Quasar visual props supplied by semantic wrappers; omitted in new theme.
//   attrs       — event listeners, aria-*, data-*, etc.; always forwarded in both themes.
const effectiveProps = computed(() => {
  const forwardedAttrs = isUnavailable.value ? attrsWithoutClick.value : attrs;
  const base = {
    icon: props.icon,
    disable: props.disable,
    loading: props.loading,
    ...(isUnavailable.value && { 'aria-disabled': 'true', ripple: false }),
    ...(props.to !== null && { to: props.to }),
    ...(props.replace && { replace: true }),
    ...(props.href !== null && { href: props.href }),
    ...(props.target !== null && { target: props.target }),
    ...(props.target === '_blank' && attrs.rel == null && { rel: 'noopener noreferrer' }),
  };

  const merged = hasTheme.value
    ? { ...base, unelevated: true, ...forwardedAttrs }
    : {
      ...base,
      size: props.size,
      round: props.shape === 'round',
      ...props.legacyProps,
      ...forwardedAttrs,
    };

  if (!isUnavailable.value) {
    return merged;
  }
  /*
   * A destination is a second way to activate the button, and the one the browser owns: withholding
   * the click leaves a router link or an href to navigate anyway. Taken off last, after every
   * channel has had its say — legacyProps is an escape hatch into the whole of q-btn's API, so a
   * destination removed earlier can be put back by one. It also stops Quasar rendering the button
   * as an anchor, which is the one tag it marks with aria-disabled instead of disabling natively.
   */
  return Object.fromEntries(
    Object.entries(merged).filter(([key]) => !DESTINATION_PROPS.includes(key)),
  );
});
</script>
<template>
  <hr-skeleton
    v-if="props.pending"
    :type="props.shape === 'square' ? 'rect' : 'circle'"
    :width="`var(--app-comp-icon-btn-container-size-${props.size})`"
    :height="`var(--app-comp-icon-btn-container-size-${props.size})`"
  />
  <q-btn
    v-else
    v-bind="effectiveProps"
    :class="[hasTheme ? classes : undefined, isUnavailable && 'hr-icon-btn--unavailable']"
  >
    <!-- @slot default Tooltip or supplementary content overlaid on the button. -->
    <slot />
  </q-btn>
</template>
<style lang="scss" scoped>

/*
 * Quasar's own disabled treatment, applied by hand: the button is not natively disabled, because
 * one receives no mouse events and the tooltip holding the reason would never open. Not scoped to
 * a theme — a control that cannot be used has to look that way in the legacy theme too.
 */
.hr-icon-btn--unavailable {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Only applies to light/dark themes */
.hr-icon-btn {
  --_container-size: var(--app-comp-icon-btn-container-size-md);
  --_width-ratio: 1;

  min-height: var(--_container-size);
  min-width: calc(var(--_container-size) * var(--_width-ratio));
  padding: 0;
  text-transform: none;
}

.hr-icon-btn--standard {
  background: transparent;
  color: var(--_icon-btn-standard-color, var(--app-primary));
}

.hr-icon-btn--filled {
  background: var(--app-primary);
  color: var(--app-on-primary);
}

.hr-icon-btn--tonal {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-icon-btn--outlined {
  background: transparent;
  color: var(--app-primary);
  border: 1px solid var(--app-outline);
}

.hr-icon-btn--shape-round {
  border-radius: var(--app-shape-corner-full);
}

.hr-icon-btn--shape-square {
  border-radius: var(--app-shape-corner-small);
}

.hr-icon-btn--size-xs {
  --_container-size: var(--app-comp-icon-btn-container-size-xs);

  :deep(.q-icon) { font-size: var(--app-comp-icon-btn-icon-size-xs); }
}

.hr-icon-btn--size-sm {
  --_container-size: var(--app-comp-icon-btn-container-size-sm);

  :deep(.q-icon) { font-size: var(--app-comp-icon-btn-icon-size-sm); }
}

.hr-icon-btn--size-md {
  --_container-size: var(--app-comp-icon-btn-container-size-md);

  :deep(.q-icon) { font-size: var(--app-comp-icon-btn-icon-size-md); }
}

.hr-icon-btn--size-lg {
  --_container-size: var(--app-comp-icon-btn-container-size-lg);

  :deep(.q-icon) { font-size: var(--app-comp-icon-btn-icon-size-lg); }
}

.hr-icon-btn--size-xl {
  --_container-size: var(--app-comp-icon-btn-container-size-xl);

  :deep(.q-icon) { font-size: var(--app-comp-icon-btn-icon-size-xl); }
}

.hr-icon-btn--width-narrow { --_width-ratio: var(--app-comp-icon-btn-width-ratio-narrow); }
.hr-icon-btn--width-wide   { --_width-ratio: var(--app-comp-icon-btn-width-ratio-wide); }

.hr-icon-btn--standard:hover::before {
  background: var(--_icon-btn-standard-color, var(--app-primary));
  opacity: 0.08;
}

.hr-icon-btn--filled:hover::before,
.hr-icon-btn--tonal:hover::before,
.hr-icon-btn--outlined:hover::before {
  background: var(--app-primary);
  opacity: 0.08;
}

.hr-icon-btn.disabled {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* Standard buttons have no intrinsic color — inherit from container and dim it,
   so disabled state works correctly on colored surfaces (e.g. nav drawer). */
.hr-icon-btn--standard.disabled {
  background: transparent;
  color: color-mix(in srgb, currentColor 38%, transparent);
}

.hr-icon-btn--outlined.disabled {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

.hr-icon-btn--destructive {
  color: var(--app-error);
}

.hr-icon-btn--destructive.hr-icon-btn--filled {
  background: var(--app-error);
  color: var(--app-on-error);
}

.hr-icon-btn--destructive.hr-icon-btn--tonal {
  background: var(--app-error-container);
  color: var(--app-on-error-container);
}

.hr-icon-btn--destructive.hr-icon-btn--outlined {
  border-color: var(--app-error);
}

.hr-icon-btn--destructive:hover::before {
  background: var(--app-error);
}
</style>
